import { BreakpointObserver, MediaMatcher } from '@angular/cdk/layout';
import { CommonModule } from '@angular/common';
import {
  Component,
  OnDestroy,
  OnInit,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { MatSidenav, MatSidenavContent } from '@angular/material/sidenav';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { TablerIconsModule } from 'angular-tabler-icons';
import { NgScrollbarModule } from 'ngx-scrollbar';
import { Subject, Subscription } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { AppSettings } from 'src/app/config';
import { MaterialModule } from 'src/app/material.module';
import { CoreService } from 'src/app/services/core.service';
import { AuthService } from '../../services/auth.service';
import { IProfileManagementService } from '../../services/business/IProfileManagementService';
import { SharedProfileData } from '../../services/data/shared-profile-data';
import { DynamicNavService } from '../../services/dynamic-nav.service';
import { NavService } from '../../services/nav.service';
import { canAccessFiraCalculator } from '../../services/route.guards';
import { isAdminUser } from '../../shared/utils/admin.util';
import { resolveVendorBankingUiMode } from '../../shared/vendor-ui/vendor-ui-capability.util';
import { VendorBankingUiMode } from '../../types/VendorUi';
import { AppHorizontalHeaderComponent } from './horizontal/header/header.component';
import { AppHorizontalSidebarComponent } from './horizontal/sidebar/sidebar.component';
import { AppBreadcrumbComponent } from './shared/breadcrumb/breadcrumb.component';
import { CustomizerComponent } from './shared/customizer/customizer.component';
import { HeaderComponent } from './vertical/header/header.component';
import { BrandingComponent } from './vertical/sidebar/branding.component';
import { NavSection } from './vertical/sidebar/nav-item/nav-item';
import { AppNavItemComponent } from './vertical/sidebar/nav-item/nav-item.component';
import { navItems } from './vertical/sidebar/sidebar-data';
import { navSections } from './vertical/sidebar/sidebar-sections';
import { SidebarComponent } from './vertical/sidebar/sidebar.component';

const MOBILE_VIEW = 'screen and (max-width: 768px)';
const TABLET_VIEW = 'screen and (min-width: 769px) and (max-width: 1024px)';
const MONITOR_VIEW = 'screen and (min-width: 1024px)';
const BELOWMONITOR = 'screen and (max-width: 1023px)';

// for mobile app sidebar
interface apps {
  id: number;
  color: string;
  icon: string;
  title: string;
  subtitle: string;
  link: string;
}

interface quicklinks {
  id: number;
  title: string;
  link: string;
}

@Component({
  selector: 'app-full',
  standalone: true,
  imports: [
    RouterModule,
    AppNavItemComponent,
    MaterialModule,
    CommonModule,
    SidebarComponent,
    NgScrollbarModule,
    TablerIconsModule,
    HeaderComponent,
    AppHorizontalHeaderComponent,
    AppHorizontalSidebarComponent,
    AppBreadcrumbComponent,
    CustomizerComponent,
    BrandingComponent,
  ],
  templateUrl: './full.component.html',
  styleUrls: [],
  encapsulation: ViewEncapsulation.None,
})
export class FullComponent implements OnInit, OnDestroy {
  navItems = navItems;
  navSections: NavSection[] = navSections;

  private destroy$ = new Subject<void>();
  /** Cached for nav updates when SharedProfileData profile changes */
  private affiliateManagerCached = false;
  private platformAdminCached = false;
  private showFiraCalculatorCached = false;

  @ViewChild('leftsidenav')
  public sidenav: MatSidenav;
  resView = false;
  @ViewChild('content', { static: true }) content!: MatSidenavContent;
  //get options from service
  options = this.settings.getOptions();
  private layoutChangesSubscription = Subscription.EMPTY;
  private isMobileScreen = false;
  private isContentWidthFixed = true;
  private isCollapsedWidthFixed = false;
  private htmlElement!: HTMLHtmlElement;

  get isOver(): boolean {
    return this.isMobileScreen;
  }

  get isTablet(): boolean {
    return this.resView;
  }

  // for mobile app sidebar
  apps: apps[] = [
    {
      id: 1,
      icon: 'solar:chat-line-line-duotone',
      color: 'primary',
      title: 'Chat Application',
      subtitle: 'Messages & Emails',
      link: '/apps/chat',
    },
    {
      id: 2,
      icon: 'solar:checklist-minimalistic-line-duotone',
      color: 'accent',
      title: 'Todo App',
      subtitle: 'Completed task',
      link: '/apps/todo',
    },
    {
      id: 3,
      icon: 'solar:bill-list-line-duotone',
      color: 'success',
      title: 'Invoice App',
      subtitle: 'Get latest invoice',
      link: '/invoices',
    },
    {
      id: 4,
      icon: 'solar:calendar-line-duotone',
      color: 'error',
      title: 'Calendar App',
      subtitle: 'Get Dates',
      link: '/apps/calendar',
    },
    {
      id: 5,
      icon: 'solar:smartphone-2-line-duotone',
      color: 'warning',
      title: 'Contact Application',
      subtitle: '2 Unsaved Contacts',
      link: '/apps/contacts',
    },
    {
      id: 6,
      icon: 'solar:ticket-line-duotone',
      color: 'primary',
      title: 'Tickets App',
      subtitle: 'Create new ticket',
      link: '/apps/tickets',
    },
    {
      id: 7,
      icon: 'solar:letter-line-duotone',
      color: 'accent',
      title: 'Email App',
      subtitle: 'Get new emails',
      link: '/apps/email/inbox',
    },
    {
      id: 8,
      icon: 'solar:book-2-line-duotone',
      color: 'warning',
      title: 'Courses',
      subtitle: 'Create new course',
      link: '/apps/courses',
    },
  ];
  quicklinks: quicklinks[] = [
    {
      id: 1,
      title: 'Pricing Page',
      link: '/theme-pages/pricing',
    },
    {
      id: 2,
      title: 'Authentication Design',
      link: '/authentication/login',
    },
    {
      id: 3,
      title: 'Register Now',
      link: '/authentication/side-register',
    },
    {
      id: 4,
      title: '404 Error Page',
      link: '/authentication/error',
    },
    {
      id: 5,
      title: 'Notes App',
      link: '/apps/notes',
    },
    {
      id: 6,
      title: 'Employee App',
      link: '/apps/employee',
    },
    {
      id: 7,
      title: 'Todo Application',
      link: '/apps/todo',
    },
  ];

  constructor(
    private settings: CoreService,
    private mediaMatcher: MediaMatcher,
    private router: Router,
    private breakpointObserver: BreakpointObserver,
    private navService: NavService,
    private _sharedProfileData: SharedProfileData,
    private profileService: IProfileManagementService,
    private dynamicNavService: DynamicNavService,
    private authService: AuthService,
  ) {
    this.htmlElement = document.querySelector('html')!;
    this.layoutChangesSubscription = this.breakpointObserver
      .observe([MOBILE_VIEW, TABLET_VIEW, MONITOR_VIEW, BELOWMONITOR])
      .subscribe((state) => {
        // SidenavOpened must be reset true when layout changes
        this.options.sidenavOpened = true;
        this.isMobileScreen = state.breakpoints[BELOWMONITOR];
        if (this.options.sidenavCollapsed == false) {
          this.options.sidenavCollapsed = state.breakpoints[TABLET_VIEW];
        }
        this.isContentWidthFixed = state.breakpoints[MONITOR_VIEW];
        this.resView = state.breakpoints[BELOWMONITOR];
      });

    // Initialize project theme with options
    this.receiveOptions(this.options);

    // This is for scroll to top
    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe((e) => {
        this.content.scrollTo({ top: 0 });
      });
  }

  async ngOnInit(): Promise<void> {
    // Subscribe to dynamic navigation changes
    this.dynamicNavService
      .getNavSections()
      .pipe(takeUntil(this.destroy$))
      .subscribe((sections) => {
        this.navSections = sections;
      });

    await this.initializeNavigation();

    this._sharedProfileData
      .getUserProfile$()
      .pipe(takeUntil(this.destroy$))
      .subscribe((profile) => {
        if (!profile) {
          return;
        }
        const hideDeposits =
          resolveVendorBankingUiMode(profile) ===
          VendorBankingUiMode.LegacyPending;
        this.dynamicNavService.updateNavSections(
          this.affiliateManagerCached,
          hideDeposits,
          this.platformAdminCached,
          this.showFiraCalculatorCached,
        );
      });
  }

  ngOnDestroy() {
    this.layoutChangesSubscription.unsubscribe();
    this.destroy$.next();
    this.destroy$.complete();
  }

  toggleCollapsed() {
    this.isContentWidthFixed = false;
    this.options.sidenavCollapsed = !this.options.sidenavCollapsed;
    this.resetCollapsedState();
  }

  resetCollapsedState(timer = 400) {
    setTimeout(() => this.settings.setOptions(this.options), timer);
  }

  onSidenavClosedStart() {
    this.isContentWidthFixed = false;
  }

  onSidenavOpenedChange(isOpened: boolean) {
    this.isCollapsedWidthFixed = !this.isOver;
    this.options.sidenavOpened = isOpened;
    this.settings.setOptions(this.options);
  }

  receiveOptions(options: AppSettings): void {
    this.options = options;
    this.toggleDarkTheme(options);
  }

  toggleDarkTheme(options: AppSettings) {
    if (options.theme === 'dark') {
      this.htmlElement.classList.add('dark-theme');
      this.htmlElement.classList.remove('light-theme');
    } else {
      this.htmlElement.classList.remove('dark-theme');
      this.htmlElement.classList.add('light-theme');
    }
  }

  /**
   * Initialize navigation based on user permissions
   */
  private async initializeNavigation(): Promise<void> {
    try {
      const cognitoUser = await this.authService.getCurrentAuthenticatedUser();
      const userProfile = await this.profileService.getUserProfile();
      const isAffiliateManager = userProfile?.affiliateManager ?? false;
      const isPlatformAdmin = isAdminUser(userProfile, cognitoUser);
      this.affiliateManagerCached = isAffiliateManager;
      this.platformAdminCached = isPlatformAdmin;
      const showFiraCalculator = await canAccessFiraCalculator(
        userProfile,
        this.profileService,
      );
      this.affiliateManagerCached = isAffiliateManager;
      this.showFiraCalculatorCached = showFiraCalculator;

      console.log('Full Component - User Profile:', userProfile);
      console.log('Full Component - Is Affiliate Manager:', isAffiliateManager);
      console.log('Full Component - Is Platform Admin:', isPlatformAdmin);

      let hideDepositsForLegacyVendor = false;
      if (!isPlatformAdmin) {
        try {
          const businessProfile = await this.profileService.getProfile();
          hideDepositsForLegacyVendor =
            resolveVendorBankingUiMode(businessProfile) ===
            VendorBankingUiMode.LegacyPending;
        } catch {
          hideDepositsForLegacyVendor = false;
        }
      }

      this.dynamicNavService.updateNavSections(
        isAffiliateManager,
        hideDepositsForLegacyVendor,
        isPlatformAdmin,
        showFiraCalculator,
      );
    } catch (error) {
      console.error('Error initializing navigation:', error);
    }
  }
}
